import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { logout } from "../../../actions/user";
import ApiClient from "../../../methods/api/apiClient";
import { isGuestMode } from "../../../methods/guestMode";
import socket from "../../../config/ChatSocket/socket";
import Html from "./Html";
import "./style.scss";

import { memo } from 'react';

const Header = memo(function Header({ setIsOpen, isOpen, particularData, isMobileView, hasSidebar, onOpenMobileSidebar }) {
  const user = useSelector((state) => state.user);
  const toggle = () => {
    setIsOpen(!isOpen);
    localStorage.setItem("sidebar", !isOpen);
  };
  const [isOpen1, setIsOpen1] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [messageCount, setUnreadMessagesCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  // L'enveloppe est visible tant qu'il reste des messages non lus.
  // Elle clignote UNIQUEMENT tant que l'utilisateur n'a pas encore visité
  // /chat (flag persistant sur la session). Une fois visité, elle reste
  // affichée (sans clignoter) et disparaît quand tous les messages sont lus.
  const visitedChatRef = useRef(false);
  const setVisitedChat = () => {
    if (visitedChatRef.current) return;
    visitedChatRef.current = true;
    try {
      const uid = user?._id || user?.id;
      sessionStorage.setItem(uid ? `anyhomes_chat_visited_${uid}` : "anyhomes_chat_visited", "1");
    } catch {}
  };
  // Restaure le flag "déjà visité /chat" pour l'utilisateur courant (session).
  useEffect(() => {
    const uid = user?._id || user?.id;
    let persisted = false;
    try {
      persisted = sessionStorage.getItem(uid ? `anyhomes_chat_visited_${uid}` : "anyhomes_chat_visited") === "1";
    } catch {}
    visitedChatRef.current = persisted;
  }, [user?._id, user?.id, user?.loggedIn, user?.isGuest]);
  const history = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const searchState = { data: "" };

  const Logout = () => {
    dispatch(logout());
    localStorage.removeItem("persist:admin-app");
    localStorage.removeItem("token");
    localStorage.removeItem("access_token");
    localStorage.removeItem("guestMode");
    localStorage.removeItem("debugMockUser");
    history("/login");
  };


  useEffect(() => {
    window.scrollTo({ top: 0 });
    // if (searchState.data) {
    //   dispatch(search_success(''))
    // }
  }, []);

  // Récupère le nombre total de messages non lus auprès du backend
  // (somme des `unread_count` sur toutes les propriétés du chat).
  useEffect(() => {
    const guest = isGuestMode() || user?.isGuest;
    if (!user?.loggedIn || guest || !user?._id) {
      setUnreadMessagesCount(0);
      return;
    }

    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const res = await ApiClient.get("chat/property-chats", {
          login_user_id: user._id,
          user_id: user._id,
        });
        if (cancelled) return;
        if (res?.success) {
          const list = res?.data?.data || [];
          const total = list.reduce((acc, itm) => acc + (Number(itm?.unread_count) || 0), 0);
          setUnreadMessagesCount(total);
        }
      } catch (err) {
        if (!cancelled) console.error("Failed to fetch unread chat count", err);
      }
    };

    fetchUnread();
    const intervalId = setInterval(fetchUnread, 60000);

    // Rafraîchissement instantané sur événements socket :
    // - nouveau message reçu → +1 unread → l'enveloppe réapparaît
    // - message marqué lu (par moi ou un autre device) → -1 unread → à 0, l'enveloppe disparaît
    const onReceive = (msg) => {
      if (cancelled) return;
      // On ignore les messages que j'envoie moi-même.
      if (msg?.sender && user?._id && String(msg.sender) === String(user._id)) return;
      fetchUnread();
    };
    const onRead = () => {
      if (!cancelled) fetchUnread();
    };
    // Le backend émet `notify-message` après un read-all / un nouveau message :
    // il porte un total d'unread (unread_count) ou un compteur par pièce
    // (count_room_chat) → on rafraîchit l'enveloppe immédiatement.
    const onNotify = (res) => {
      if (cancelled || !res?.data) return;
      const data = res.data;
      if (data.user_id && user?._id && String(data.user_id) !== String(user._id)) return;
      if (typeof data.unread_count === "number") setUnreadMessagesCount(data.unread_count);
      else if (typeof data.count_room_chat === "number" && typeof data.room_id === "string") fetchUnread();
      else if (data.count_room_chat !== undefined) fetchUnread();
      else fetchUnread();
    };
    const onReadAll = () => {
      if (!cancelled) fetchUnread();
    };
    socket.on("receive-message", onReceive);
    socket.on("read-message", onRead);
    socket.on("read-all-message", onReadAll);
    socket.on("notify-message", onNotify);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      socket.off("receive-message", onReceive);
      socket.off("read-message", onRead);
      socket.off("read-all-message", onReadAll);
      socket.off("notify-message", onNotify);
    };
  }, [user?._id, user?.loggedIn, user?.isGuest, location?.pathname]);

  // Ouvre la messagerie depuis le header : on considère /chat comme visité.
  const openMessages = () => {
    setVisitedChat();
    history("/chat");
  };

  // Arrivée sur /chat (via le bouton Message de la sidebar ou tout autre lien)
  // → l'enveloppe cesse de clignoter (elle reste visible tant que des messages
  // non lus existent).
  useEffect(() => {
    if (location?.pathname && location.pathname.startsWith("/chat")) {
      setVisitedChat();
    }
  }, [location?.pathname]);

  // Compteur de notifications non lues — alimenté par le socket `un-noti`
  // (même mécanisme que PageLayout). Le backend renvoie data.unread_count.
  useEffect(() => {
    const guest = isGuestMode() || user?.isGuest;
    const userId = user?._id || user?.id;
    if (!user?.loggedIn || guest || !userId) {
      setNotificationCount(0);
      return;
    }

    const requestCount = () => socket.emit("un-noti", { user_id: userId });
    const onUnNoti = (res) => {
      const count = Number(res?.data?.unread_count) || 0;
      setNotificationCount(count);
    };

    socket.on("un-noti", onUnNoti);
    requestCount();
    // Refresh périodique en filet de sécurité
    const intervalId = setInterval(requestCount, 60000);

    return () => {
      socket.off("un-noti", onUnNoti);
      clearInterval(intervalId);
    };
  }, [user?._id, user?.id, user?.loggedIn, user?.isGuest, location?.pathname]);

  useEffect(() => {
    setSearch(searchState.data);
  }, [searchState]);

  const [search, setSearch] = useState("");

  const searchHandle = (e) => {
    e.preventDefault();
    // dispatch(search_success(search))
  };

  const searchChange = (e) => {
    setSearch(e);
    if (!e) {
      // dispatch(search_success(''))
    }
  };

  const clear = () => {
    setSearch("");
    // dispatch(search_success(''))
  };

    return (
      <Html
        isOpen={isOpen}
        toggle={toggle}
        searchHandle={searchHandle}
        search={search}
        user={user}
        searchChange={searchChange}
        isOpen1={isOpen1}
        clear={clear}
        Logout={Logout}
        messageCount={messageCount}
        messageBlink={messageCount > 0 && !visitedChatRef.current}
        notificationCount={notificationCount}
        onOpenMessages={openMessages}
        particularData={particularData}
        showAccountMenu={showAccountMenu}
        setShowAccountMenu={setShowAccountMenu}
        isMobileView={isMobileView}
        hasSidebar={hasSidebar}
        onOpenMobileSidebar={onOpenMobileSidebar}
      />
    );
});

export default Header;
